<script setup>
import { computed, ref } from "vue";
import { useMutation } from "@tanstack/vue-query";
import axios from "axios";
import { errorToast, successToast } from "@/composables/useToasts";
import BaseModal from "@/components/global-themed/BaseModal.vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import DefaultInput from "@/components/global-themed/inputs/DefaultInput.vue";
import DefaultTextarea from "@/components/global-themed/inputs/DefaultTextarea.vue";
import AccordionTransition from "@/components/transitions/AccordionTransition.vue";
import {platformName} from "@/composables/useLocalDomains";

const props = defineProps({
  websiteId: {
    type: Number,
    required: true,
  },
  signature: {
    type: String,
    default: "",
  },
  signatureInput: {
    type: String,
    default: "",
  },
  signatureAgent: {
    type: String,
    default: "",
  },
});

const SHOPIFY_HELP_URL =
  "https://help.shopify.com/en/manual/promoting-marketing/seo/crawling-your-store#create";

const signature = ref(props.signature);
const signatureInput = ref(props.signatureInput);
const signatureAgent = ref(props.signatureAgent);
const showInstructions = ref(false);
const expanded = ref(false);

const expiresAt = computed(() => {
  const match = /(?:^|[;\s])expires=(\d+)/.exec(signatureInput.value);

  if (!match) {
    return null;
  }

  const expiryDate = new Date(Number(match[1]) * 1000);

  if (Number.isNaN(expiryDate.getTime())) {
    return null;
  }

  return expiryDate;
});

const formattedExpiry = computed(() => {
  if (!expiresAt.value) {
    return null;
  }

  return expiresAt.value.toLocaleString();
});

const hasExpired = computed(() => {
  if (!expiresAt.value) {
    return false;
  }

  return expiresAt.value.getTime() < Date.now();
});

const canSave = computed(() => {
  return (
    signature.value.trim().length > 0 &&
    signatureInput.value.trim().length > 0 &&
    signatureAgent.value.trim().length > 0
  );
});

const { mutate: saveSignature, isPending } = useMutation({
  mutationFn: () =>
    axios.post(`/website/settings/${props.websiteId}/web-bot-auth-signature`, {
      signature: signature.value.trim(),
      signature_input: signatureInput.value.trim(),
      signature_agent: signatureAgent.value.trim(),
    }),
  onSuccess: () => {
    successToast("Web Bot Auth signature saved successfully.");
  },
  onError: () => {
    errorToast("Something went wrong saving your signature. Please try again.");
  },
});
</script>

<template>
  <div class="tailwind">
    <div class="tw-flex tw-flex-col tw-gap-4 md:tw-flex-row md:tw-items-start md:tw-justify-between">
      <div class="tw-flex tw-flex-col tw-gap-1 md:tw-flex-1">
        <p class="tw-flex tw-items-center tw-gap-2 tw-font-bold tw-text-neutral-800">
          Web Bot Auth Signature
          <a
            :href="SHOPIFY_HELP_URL"
            target="_blank"
            rel="noopener"
            class="tooltipster tw-text-primary hover:tw-text-primary-600"
            title="Learn how to create a Web Bot Auth signature in Shopify"
          >
            <i class="fad fa-info-circle" />
          </a>
        </p>
        <p class="tw-text-sm tw-text-neutral-600">
          Allowlist the crawler by adding the three Web Bot Auth signature
          headers issued by Shopify.
        </p>
      </div>

      <DefaultButton
        variant="primary-inverted"
        size="small"
        additional-classes="tw-shrink-0 tw-whitespace-nowrap md:tw-mt-1"
        @click="showInstructions = true"
      >
        Setup instructions
      </DefaultButton>
    </div>

    <div class="tw-mt-4">
      <button
        type="button"
        class="
          tw-flex tw-w-full tw-items-center tw-justify-between tw-rounded-lg tw-border tw-border-neutral-300
          tw-bg-neutral-100 tw-px-4 tw-py-3 tw-text-left tw-text-sm tw-font-semibold tw-text-neutral-800
        "
        :aria-expanded="expanded"
        @click="expanded = !expanded"
      >
        <span>Signature headers</span>
        <SvgVue
          icon="icons/chevron-thick"
          class="tw-transition-transform tw-h-3"
          :class="{ 'tw-rotate-180': expanded }"
        />
      </button>

      <AccordionTransition>
        <div
          v-if="expanded"
          class="tw-overflow-hidden tw-transition-all tw-duration-300"
        >
          <div class="tw-pt-4">
            <div class="tw-mb-4 tw-flex tw-flex-col tw-gap-4">
              <DefaultTextarea
                id="web-bot-auth-signature"
                v-model="signature"
                auto-expand
                label="Signature"
                placeholder="sig1=:…:"
              />

              <DefaultTextarea
                id="web-bot-auth-signature-input"
                v-model="signatureInput"
                auto-expand
                label="Signature-Input"
                placeholder="sig1=(&quot;@authority&quot;);created=…;expires=…;keyid=&quot;…&quot;"
              />

              <DefaultInput
                id="web-bot-auth-signature-agent"
                v-model="signatureAgent"
                auto-expand
                label="Signature-Agent"
                placeholder="https://…"
              />
            </div>

            <div
              v-if="formattedExpiry"
              class="tw-mb-4 tw-flex tw-items-center tw-gap-2 tw-text-sm"
              :class="hasExpired ? 'tw-text-danger' : 'tw-text-neutral-700'"
            >
              <SvgVue
                icon="icons/clock"
                class="tw-h-4"
              />
              <span v-if="hasExpired">
                <strong>Signature expired on {{ formattedExpiry }}.</strong>
                Please renew it in Shopify and update it here.
              </span>
              <span v-else>
                Signature valid until <strong>{{ formattedExpiry }}</strong>.
              </span>
            </div>

            <DefaultButton
              variant="primary"
              :loading="isPending"
              :disabled="isPending || !canSave"
              @click="saveSignature()"
            >
              {{ isPending ? "Saving…" : "Save signature" }}
            </DefaultButton>
          </div>
        </div>
      </AccordionTransition>
    </div>
  </div>

  <Teleport to=".tailwind">
    <BaseModal
      v-if="showInstructions"
      :modal-class="'tw-max-w-[640px] tw-text-neutral-900'"
      @close="showInstructions = false"
    >
      <h2 class="tw-mb-4 tw-text-2xl tw-font-bold">
        Creating a Web Bot Auth signature
      </h2>
      <ol class="tw-mb-6 tw-list-decimal tw-space-y-3 tw-pl-5">
        <li>
          In your Shopify admin, go to
          <strong>Online Store &rarr; Preferences</strong> and find the
          crawler allowlist / Web Bot Auth section.
        </li>
        <li>
          Add {{ platformName }} as an allowed crawler to generate a signature. Shopify will
          give you three values: <strong>Signature</strong>,
          <strong>Signature-Input</strong> and
          <strong>Signature-Agent</strong>.
        </li>
        <li>
          Copy each value into the matching field on this page. The
          <strong>Signature-Input</strong> contains the expiry time, which we'll
          display so you know when to renew.
        </li>
        <li>
          Click <strong>Save signature</strong>. Recrawl your site so the
          crawler starts sending the signature headers.
        </li>
      </ol>
      <p class="tw-mb-6">
        Full instructions are available in Shopify's
        <a
          :href="SHOPIFY_HELP_URL"
          target="_blank"
          rel="noopener"
          class="tw-text-primary tw-underline"
        >
          help documentation</a>.
      </p>
      <div class="tw-flex tw-justify-end">
        <DefaultButton
          variant="primary"
          @click="showInstructions = false"
        >
          Got it
        </DefaultButton>
      </div>
    </BaseModal>
  </Teleport>
</template>
